import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography
} from '@mui/material';
import { Plus, Edit2, Trash2, Split } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import ConfirmModal from '../ConfirmModal';

export default function ChannelManagement() {
  const [channels, setChannels] = useState([]);
  const [plcs, setPlcs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteChannelId, setDeleteChannelId] = useState(null);

  const [formData, setFormData] = useState({
    channelName: '',
    plcId: '',
    start: 0,
    length: 1,
    type: 0,
    dbNo: 0,
    interval: 1000,
    opcType: 0
  });

  useEffect(() => {
    const loadData = async () => {
      const plcsData = await loadPlcs(); // Load PLCs first
      await loadChannels(plcsData); // Then load channels with PLCs data
    };
    loadData();
  }, []);

  const loadChannels = async (plcsData = plcs) => {
    setLoading(true);
    try {
      const channelsData = await api.dataSources.getAllChannels();
      // Convert backend field names to frontend field names
      const convertedChannels = (channelsData || []).map(channel => {
        // Use PlcName from API or fallback to plcsData lookup
        const plcIdValue = Number(channel.plcID ?? channel.PlcID ?? channel.plcId ?? 0);
        const plcInfo = plcsData.find(p => p.plcId === plcIdValue);
        return {
          channelId: Number(channel.channelID ?? channel.ChannelID ?? channel.channelId ?? 0),
          channelName: String(channel.channelName ?? channel.ChannelName ?? ''),
          plcId: plcIdValue,
          plcName: String(channel.plcName ?? channel.PlcName ?? plcInfo?.plcName ?? `PLC ${plcIdValue}`),
          start: Number(channel.start ?? channel.Start ?? 0),
          length: Number(channel.length ?? channel.Length ?? 50),
          type: Number(channel.type ?? channel.Type ?? 0),
          dbNo: Number(channel.dbNo ?? channel.DBNo ?? 0),
          interval: Number(channel.interval ?? channel.Interval ?? 5000),
          opcType: Number(channel.opcType ?? channel.OpcType ?? 0),
          tagCount: Number(channel.tagCount ?? channel.TagCount ?? 0),
          lastUpdate: new Date(channel.updatedAt ?? channel.UpdatedAt ?? new Date()).toLocaleString('tr-TR')
        };
      });
      setChannels(convertedChannels);
    } catch (error) {
      console.error('Error loading channels:', error);
      setChannels([]);
      toast.error('Kanallar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadPlcs = async () => {
    try {
      const plcsData = await api.dataSources.getAllPlcs();
      // Convert backend field names to frontend field names
      const convertedPlcs = plcsData.map(plc => ({
        plcId: plc.plcID ?? plc.PlcID ?? plc.plcId,
        plcName: plc.plcName ?? plc.PlcName,
        plcAddress: plc.plcAddress ?? plc.PlcAddress,
        isActive: plc.isActive ?? plc.IsActive
      }));
      setPlcs(convertedPlcs);
      return convertedPlcs;
    } catch (error) {
      console.error('Error loading PLCs:', error);
      // Fallback to mock PLCs data
      const mockPlcs = [
        { plcId: 1, plcName: 'Main Production PLC', plcAddress: '192.168.1.100', isActive: true },
        { plcId: 2, plcName: 'Packaging Line PLC', plcAddress: '192.168.1.101', isActive: false }
      ];
      setPlcs(mockPlcs);
      return mockPlcs;
    }
  };

  const handleOpenDialog = (channel = null) => {
    if (channel) {
      setSelectedChannel(channel);
      setFormData({
        channelName: channel.channelName,
        plcId: channel.plcId,
        start: channel.start,
        length: channel.length,
        type: channel.type,
        dbNo: channel.dbNo,
        interval: channel.interval,
        opcType: channel.opcType
      });
    } else {
      setSelectedChannel(null);
      setFormData({
        channelName: '',
        plcId: '',
        start: 0,
        length: 1,
        type: 0,
        dbNo: 0,
        interval: 1000,
        opcType: 0
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedChannel(null);
  };

  const handleSave = async () => {
    try {
      if (selectedChannel) {
        // Update existing channel
        const updatedChannel = await api.dataSources.updateChannel({ ...selectedChannel, ...formData });
        setChannels(channels.map(c => c.channelId === selectedChannel.channelId ? updatedChannel : c));
        toast.success('Kanal başarıyla güncellendi');
      } else {
        // Create new channel
        const newChannel = await api.dataSources.createChannel(formData);
        setChannels([...channels, newChannel]);
        toast.success('Kanal başarıyla eklendi');
      }
      handleCloseDialog();
      await loadChannels(); // Refresh the list
    } catch (error) {
      console.error('Error saving channel:', error);
      // Fallback to local state update
      if (selectedChannel) {
        const updatedChannel = { 
          ...selectedChannel, 
          ...formData,
          plcName: plcs.find(p => p.plcId === formData.plcId)?.plcName || '',
          lastUpdate: new Date().toLocaleString()
        };
        setChannels(channels.map(c => c.channelId === selectedChannel.channelId ? updatedChannel : c));
        toast.success('Kanal başarıyla güncellendi (yerel)');
      } else {
        const newChannel = {
          channelId: Math.max(...channels.map(c => c.channelId), 0) + 1,
          ...formData,
          plcName: plcs.find(p => p.plcId === formData.plcId)?.plcName || '',
          tagCount: 0,
          lastUpdate: new Date().toLocaleString('tr-TR')
        };
        setChannels([...channels, newChannel]);
        toast.success('Kanal başarıyla eklendi (yerel)');
      }
      handleCloseDialog();
    }
  };

  const handleDelete = (channelId) => {
    setDeleteChannelId(channelId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.dataSources.deleteChannel(deleteChannelId);
      setChannels(channels.filter(c => c.channelId !== deleteChannelId));
      toast.success('Kanal başarıyla silindi');
    } catch (error) {
      console.error('Error deleting channel:', error);
      setChannels(channels.filter(c => c.channelId !== deleteChannelId));
      toast.success('Kanal başarıyla silindi (yerel)');
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      1: 'Analog',
      2: 'Binary',
      3: 'Çoklu Durum',
      0: 'String'
    };
    return types[type] || 'Bilinmiyor';
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Kanallar</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`${channels.length} Kanal`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading || plcs.length === 0}
                >
                  Kanal Ekle
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Ad</TableCell>
                  <TableCell>PLC</TableCell>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Uzunluk</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>DB No</TableCell>
                  <TableCell>Aralık</TableCell>
                  <TableCell>Noktalar</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channels.map((channel) => (
                  <TableRow key={channel.channelId} hover>
                    <TableCell>{channel.channelId}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {channel.channelName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {channel.plcName}
                      </Typography>
                    </TableCell>
                    <TableCell>{channel.start}</TableCell>
                    <TableCell>{channel.length}</TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(channel.type)} size="small" />
                    </TableCell>
                    <TableCell>{channel.dbNo}</TableCell>
                    <TableCell>{channel.interval}ms</TableCell>
                    <TableCell>
                      <Chip
                        label={`${channel.tagCount} nokta`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(channel)}
                          color="primary"
                          title="Düzenle"
                        >
                          <Edit2 size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(channel.channelId)}
                          color="error"
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {channels.length === 0 && !loading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: 'text.secondary'
            }}>
              <Split size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="body1">Yapılandırılmış kanal yok</Typography>
              <Typography variant="body2">
                {plcs.length === 0 ? 'Önce PLC ekleyin, sonra kanal oluşturun' : 'Başlamak için "Kanal Ekle" butonuna tıklayın'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Kanal Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedChannel ? 'Kanalı Düzenle' : 'Kanal Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Kanal Adı"
              value={formData.channelName}
              onChange={(e) => setFormData({ ...formData, channelName: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel shrink>PLC Cihazı</InputLabel>
              <Select
                native
                value={formData.plcId != null ? String(formData.plcId) : ''}
                onChange={(e) => setFormData({ ...formData, plcId: e.target.value === '' ? null : parseInt(e.target.value) })}
                label="PLC Cihazı"
              >
                <option value="">Seçiniz...</option>
                {plcs.map((plc) => (
                  <option key={plc.plcId} value={String(plc.plcId)}>
                    {plc.plcName} {plc.plcAddress ? `(${plc.plcAddress})` : ''}
                  </option>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Başlangıç Adresi"
                type="number"
                value={formData.start}
                onChange={(e) => setFormData({ ...formData, start: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <TextField
                label="Uzunluk"
                type="number"
                value={formData.length}
                onChange={(e) => setFormData({ ...formData, length: parseInt(e.target.value) || 1 })}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Veri Tipi</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="Veri Tipi"
                >
                  <MenuItem value={0}>Analog</MenuItem>
                  <MenuItem value={1}>Binary</MenuItem>
                  <MenuItem value={2}>Çoklu Durum</MenuItem>
                  <MenuItem value={3}>String</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="DB Numarası"
                type="number"
                value={formData.dbNo}
                onChange={(e) => setFormData({ ...formData, dbNo: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Okuma Aralığı (ms)"
                type="number"
                value={formData.interval}
                onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) || 1000 })}
                fullWidth
              />
              <TextField
                label="OPC Tipi"
                type="number"
                value={formData.opcType}
                onChange={(e) => setFormData({ ...formData, opcType: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.channelName || formData.plcId == null}
          >
            {selectedChannel ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Kanalı Sil"
        content="Bu kanalı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        type="yesNo"
      />
    </Box>
  );
}